
import { Component, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import JielongIntro from "@/components/JielongIntro/index.vue";
import JielongTitleCard from "@/views/jielong/components/JielongTitleCard.vue";
import JielongGoodsCard, { createGoodsItemFromSelected, pickJoinPrice } from "@/views/jielong/components/JielongGoodsCard.vue";
import JielongSettingCard from "@/views/jielong/components/JielongSettingCard.vue";
import BottomBtnGroup from "@/views/jielong/components/BottomBtnGroup.vue";
import TipsPopup from "@/components/tipsPopup/index.vue";
import {
  JielongIntroCardDetail,
  JielongGoodsItem,
  JielongPreviewPayload,
  JIELONG_PREVIEW_STORAGE_KEY,
  JielongSettingForm,
  JielongEditMode,
  JieLongStatus,
  JIELONG_STATUS,
  pickGoodsImage,
  getJielongDetail,
  createJielong,
  updateJielong,
  serializeDetailListForApi,
  normalizeJielongDetail,
} from "@/api/jielong";

const SELECTED_GOODS_CACHE_KEY = "jielongSelectedGoods";

type JielongTab = "goods" | "setting" | "intro";

interface TabItem {
  label: string;
  value: JielongTab;
}

const SECTION_IDS: Record<JielongTab, string> = {
  goods: "section-goods",
  setting: "section-setting",
  intro: "section-intro",
};

const TAB_ORDER: JielongTab[] = ["goods", "setting", "intro"];

/** 去选品 / 预览时保留实例；系统返回回列表时清掉，避免下次「修改接龙」复用旧数据且不拉详情 */
const PRESERVE_KEEPALIVE_ROUTE_NAMES = ["SelectGoods", "JielongPreview"];

Component.registerHooks(["beforeRouteLeave"]);

@Component({
  name: "NewJielong",
  components: {
    JielongIntro,
    JielongTitleCard,
    JielongGoodsCard,
    JielongSettingCard,
    BottomBtnGroup,
    TipsPopup,
  },
})
export default class NewJielong extends Vue {
  @Getter companyId!: string;

  title = "";
  cards: JielongIntroCardDetail[] = [];
  goods: JielongGoodsItem[] = [];
  setting: JielongSettingForm = {
    isStorePickup: 1,
    isHomeDelivery: 0,
    startTime: "",
    endTime: "",
    extendTime: "",
    arrivalTime: "",
    minOrderAmount: 0,
    minOrderQuantity: 0,
  };
  activeTab: JielongTab = "goods";
  submitLoading = false;
  tipsPopupHtmlContent = "";

  editMode: JielongEditMode = "create";
  activityStatus: JieLongStatus = JIELONG_STATUS.NOT_STARTED;
  joinActivityId = "";
  detailLoading = false;
  private loadedEditId: string | null = null;

  readonly tabs: TabItem[] = [
    { label: "商品", value: "goods" },
    { label: "设置", value: "setting" },
    { label: "介绍", value: "intro" },
  ];

  get activeTabIndex (): number {
    return this.tabs.findIndex(tab => tab.value === this.activeTab);
  }

  get pageTitle (): string {
    return this.editMode === "create" ? "新建接龙" : "编辑接龙";
  }

  get submitText (): string {
    return this.editMode === "create" ? "立即发起" : "重新发起";
  }

  get isEditMode (): boolean {
    return this.editMode !== "create";
  }

  get isReadonlySections (): boolean {
    return this.editMode === "limited";
  }

  get routeEditId (): string {
    const id = this.$route.query.id;
    return typeof id === "string" ? id : Array.isArray(id) ? String(id[0] || "") : "";
  }

  private get routeType (): string {
    const type = this.$route.query.type;
    return typeof type === "string" ? type : Array.isArray(type) ? String(type[0] || "") : "";
  }

  /** 复制接龙入口：带 id + type=create，回填数据但保持新建态 */
  get isCopyCreate (): boolean {
    return this.routeType === "create" && !!this.routeEditId;
  }

  /** 草稿入口：带 id + type=draft，回填后保持 create 态 */
  get isDraftContinue (): boolean {
    return this.routeType === "draft" && !!this.routeEditId;
  }

  private observer: IntersectionObserver | null = null;
  private scrollLock = false;
  private resizeHandler: (() => void) | null = null;
  private androidKbHandler: (() => void) | null = null;
  private androidKbFocusHandler: ((e: FocusEvent) => void) | null = null;

  onTabChange (value: JielongTab): void {
    this.activeTab = value;
    const section = document.getElementById(SECTION_IDS[value]);
    const container = this.$refs.scrollEl as HTMLElement | undefined;
    if (!section || !container) return;

    this.scrollLock = true;
    container.scrollTo({
      top: Math.max(0, section.offsetTop),
      behavior: "smooth",
    });
    window.setTimeout(() => {
      this.scrollLock = false;
    }, 400);
  }

  onScroll (): void {
    // activeTab 由 IntersectionObserver 同步
  }

  /** 未填写起购金额/数量时传后端 null（表单空值为 0）；延长时间仅接龙中提交 */
  private getSettingForApi (): JielongSettingForm {
    const amount = this.setting.minOrderAmount;
    const qty = this.setting.minOrderQuantity;
    const payload: JielongSettingForm = {
      ...this.setting,
      minOrderAmount: amount != null && amount > 0 ? amount : null,
      minOrderQuantity: qty != null && qty > 0 ? qty : null,
    };
    if (this.activityStatus !== JIELONG_STATUS.ONGOING) {
      delete payload.extendTime;
    }
    return payload;
  }

  onSaveDraft (): void {
    if (this.isEditMode) return;
    if (this.submitLoading) return;
    this.submitLoading = true;

    createJielong({
      storeId: this.companyId,
      title: this.title,
      ...this.getSettingForApi(),
      goodsList: this.goods,
      detailList: serializeDetailListForApi(this.cards),
      createType: 2,
    })
      .then(() => {
        this.$toast("草稿保存成功");
        this.$store.commit("delKeepAlive", "NewJielong");
        this.$router.back();
      })
      .catch(() => {
        this.$toast("草稿保存失败，请重试");
      })
      .finally(() => {
        this.submitLoading = false;
      });
  }

  onPreview (): void {
    if (this.editMode === "limited") {
      if (!this.validateLimitedFields()) return;
    } else if (!this.validateRequiredFields()) {
      return;
    }
    const setting = this.getSettingForApi();
    const payload: JielongPreviewPayload = {
      activity: {
        title: this.title.trim() || "（未填写标题）",
        isStorePickup: setting.isStorePickup,
        isHomeDelivery: setting.isHomeDelivery,
        startTime: setting.startTime,
        endTime: setting.endTime,
        extendTime: setting.extendTime || "",
        arrivalTime: setting.arrivalTime,
        minOrderAmount: setting.minOrderAmount,
        minOrderQuantity: setting.minOrderQuantity,
        status: this.editMode === "create" ? JIELONG_STATUS.NOT_STARTED : this.activityStatus,
        orderCount: 0,
        actualIncome: 0,
        refundAmount: 0,
        joinUserCount: 0,
        cancelUserCount: 0,
        viewCount: 1,
        isDraft: this.editMode === "create" ? 1 : 0,
        joinActivityId: this.joinActivityId || undefined,
      },
      detailList: this.cards,
      goodsList: this.goods.map((g) => ({
        ...g,
        goodsImage: pickGoodsImage(g as any) || g.goodsImage || "",
      })),
      editMode: this.editMode,
    };
    window.sessionStorage.setItem(JIELONG_PREVIEW_STORAGE_KEY, JSON.stringify(payload));
    this.$router.push({ name: "JielongPreview" });
  }

  onSubmit (): void {
    if (this.editMode === "limited") {
      if (!this.validateLimitedFields()) return;
    } else if (!this.validateRequiredFields()) {
      return;
    }

    if (this.submitLoading) return;
    this.submitLoading = true;

    const done = () => {
      this.submitLoading = false;
    };

    const success = (msg = "保存成功") => {
      this.$toast(msg);
      this.$store.commit("delKeepAlive", "NewJielong");
      this.$router.back();
    };

    const fail = (msg = "保存失败，请重试") => {
      this.$toast(msg);
    };

    if (this.editMode === "create") {
      createJielong({
        storeId: this.companyId,
        title: this.title,
        ...this.getSettingForApi(),
        goodsList: this.goods,
        detailList: serializeDetailListForApi(this.cards),
        createType: 1,
      })
        .then(() => success("发起成功"))
        .catch(() => fail("发起失败，请重试"))
        .finally(done);
      return;
    }

    if (this.editMode === "limited") {
      updateJielong({
        joinActivityId: this.joinActivityId,
        storeId: this.companyId,
        updateType: 2,
        title: this.title,
        ...this.getSettingForApi(),
        goodsList: this.goods,
        detailList: serializeDetailListForApi(this.cards),
      })
        .then(() => success())
        .catch(() => { /* 错误提示由接口拦截器统一 toast */ })
        .finally(done);
      return;
    }

    updateJielong({
      joinActivityId: this.joinActivityId,
      title: this.title,
      ...this.getSettingForApi(),
      goodsList: this.goods,
      detailList: serializeDetailListForApi(this.cards),
      storeId: this.companyId,
      updateType: 1,
    })
      .then(() => success())
      .catch(() => { /* 错误提示由接口拦截器统一 toast */ })
      .finally(done);
  }

  /** 进行中：校验标题与设置（延长时间等）；商品只读字段不做全量必填 */
  private validateLimitedFields (): boolean {
    const titleOk = (this.$refs.titleCardRef as any)?.validate?.() !== false;
    const settingOk = (this.$refs.settingCardRef as any)?.validate?.() !== false;
    if (titleOk && settingOk) return true;
    this.onTabChange(!titleOk ? "goods" : "setting");
    return false;
  }

  /** 校验必填项；失败时抖动提示并滚动到首个错误区块 */
  private validateRequiredFields (): boolean {
    const titleOk = (this.$refs.titleCardRef as any)?.validate?.() !== false;
    const goodsOk = (this.$refs.goodsCardRef as any)?.validate?.() !== false;
    const settingOk = (this.$refs.settingCardRef as any)?.validate?.() !== false;

    if (titleOk && goodsOk && settingOk) return true;

    const tab: JielongTab = !titleOk || !goodsOk ? "goods" : "setting";
    const section = document.getElementById(SECTION_IDS[tab]);
    const container = this.$refs.scrollEl as HTMLElement | undefined;
    if (section && container) {
      this.activeTab = tab;
      this.scrollLock = true;
      container.scrollTo({
        top: Math.max(0, section.offsetTop),
        behavior: "smooth",
      });
      window.setTimeout(() => {
        this.scrollLock = false;
      }, 400);
    }
    return false;
  }

  onHandleBack (): void {
    // 编辑态直接返回，不弹草稿确认
    if (this.isEditMode) {
      this.$store.commit("delKeepAlive", "NewJielong");
      this.$router.back();
      return;
    }
    this.tipsPopupHtmlContent = `
      <P>接龙未发起，退出后将不保存已编</P>
      <P>辑的内容，保存后将覆盖现有草稿</P>
    `;
    (this.$refs.tipsPopupRef as any).showPopup();
  }

  onHandleBackCancel (): void {
    this.$store.commit("delKeepAlive", "NewJielong");
    this.$router.back();
  }

  private resetCreateForm (): void {
    this.editMode = "create";
    this.activityStatus = JIELONG_STATUS.NOT_STARTED;
    this.joinActivityId = "";
    this.loadedEditId = null;
    this.title = "";
    this.cards = [];
    this.goods = [];
    this.setting = {
      isStorePickup: 1,
      isHomeDelivery: 0,
      startTime: "",
      endTime: "",
      extendTime: "",
      arrivalTime: "",
      minOrderAmount: 0,
      minOrderQuantity: 0,
    };
  }

  private applyDetail (payload: JielongPreviewPayload): void {
    const { activity, detailList: details, goodsList: goods } = payload;
    const copyCreate = this.isCopyCreate;
    const isDraft = activity.isDraft === 1;
    // 复制新建 / 继续编辑草稿：均保持 create 态（标题「新建接龙」、可存草稿、提交走发起）
    const asCreate = copyCreate || isDraft;
    if (!asCreate && (activity.status === JIELONG_STATUS.ENDED || activity.status === JIELONG_STATUS.CANCELLED)) {
      this.$toast("该接龙已结束，无法编辑");
      this.$router.back();
      return;
    }
    // 复制新建：回填内容但不带原活动 id，提交走新建；草稿需保留 id 以便覆盖
    this.joinActivityId = copyCreate ? "" : (activity.joinActivityId || this.routeEditId);
    this.activityStatus = asCreate ? JIELONG_STATUS.NOT_STARTED : activity.status;
    this.editMode = asCreate
      ? "create"
      : activity.status === JIELONG_STATUS.ONGOING ? "limited" : "full";
    this.title = activity.title || "";
    this.cards = details || [];
    // 复制接龙：isJoinGoods 为 0 表示失效/非接龙商品，不回填
    const sourceGoods = copyCreate
      ? (goods || []).filter((g) => g.isJoinGoods !== 0)
      : (goods || []);
    this.goods = sourceGoods.map((g, idx) => ({
      ...g,
      // 复制时去掉原活动商品主键，避免误当成编辑
      joinActivityGoodsId: copyCreate ? undefined : g.joinActivityGoodsId,
      _key: g._key || `${copyCreate ? "copy" : "edit"}-${g.goodsId || idx}`,
    }));
    this.setting = {
      isStorePickup: activity.isStorePickup,
      isHomeDelivery: activity.isHomeDelivery,
      // 复制接龙：不沿用原接龙时间/到货时间，走新建默认时间逻辑
      startTime: copyCreate ? "" : (activity.startTime || ""),
      endTime: copyCreate ? "" : (activity.endTime || ""),
      extendTime: copyCreate ? "" : (activity.extendTime || ""),
      arrivalTime: copyCreate ? "" : (activity.arrivalTime || ""),
      minOrderAmount: activity.minOrderAmount,
      minOrderQuantity: activity.minOrderQuantity,
    };
  }

  private loadEditDetail (id: string): void {
    if (!id) {
      // 仅在本页处于新建路由时清空；去选品等子页时勿重置（见 onRouteQueryChange）
      if (this.loadedEditId != null) this.resetCreateForm();
      return;
    }
    // 复制 / 草稿 / 编辑用不同 cache key，避免互相污染
    const loadKey = this.isCopyCreate ? `copy:${id}` : this.isDraftContinue ? `draft:${id}` : id;
    if (this.loadedEditId === loadKey) return;

    // 进入/切换：先定模式文案；切 id 时清空旧表单防脏数据
    this.editMode = (this.isCopyCreate || this.isDraftContinue) ? "create" : "full";
    this.joinActivityId = this.isCopyCreate ? "" : id;
    if (this.loadedEditId != null) {
      this.title = "";
      this.cards = [];
      this.goods = [];
      this.setting = {
        isStorePickup: 1,
        isHomeDelivery: 0,
        startTime: "",
        endTime: "",
        extendTime: "",
        arrivalTime: "",
        minOrderAmount: 0,
        minOrderQuantity: 0,
      };
    }

    this.detailLoading = true;
    getJielongDetail({
      storeId: this.companyId,
      joinActivityId: id,
    })
      .then((res) => {
        this.applyDetail(normalizeJielongDetail(res.data));
        this.loadedEditId = loadKey;
        // 详情回填后再合并选品缓存，避免 activated 过早消费后被 applyDetail 覆盖
        this.applySelectedGoodsFromCache();
      })
      .catch(() => {
        this.$toast("加载接龙失败");
        this.$router.back();
      })
      .finally(() => {
        this.detailLoading = false;
      });
  }

  created (): void {
    // 保活缓存，避免"选择商品"往返时页面被销毁重建导致已填写内容丢失
    // 必须显式 name: NewJielong，否则测试/生产压缩后 class 名变化会导致 keep-alive 失效
    this.$store.commit("addKeepAlive", "NewJielong");
    this.loadEditDetail(this.routeEditId);
    // 新建无详情：重建时也要读选品缓存（有 keep-alive 时由 activated 再读一次，无缓存则直接 return）
    if (!this.routeEditId) {
      this.applySelectedGoodsFromCache();
    }
  }

  mounted (): void {
    this.$nextTick(() => {
      this.setupObserver();
    });
    this.resizeHandler = () => this.setupObserver();
    window.addEventListener("resize", this.resizeHandler);
    this.setupAndroidKeyboardGuard();
  }

  activated (): void {
    // keep-alive 回前台：实例还在则详情不必重拉；详情加载中勿先消费缓存（等 then 里合并）
    if (!this.detailLoading) {
      this.applySelectedGoodsFromCache();
    }
    this.$nextTick(() => {
      this.setupObserver();
    });
  }

  @Watch("$route.query", { deep: true })
  onRouteQueryChange (): void {
    if (this.$route.name !== "NewJielong") return;
    this.loadEditDetail(this.routeEditId);
  }

  beforeRouteLeave (to, _from, next): void {
    if (!PRESERVE_KEEPALIVE_ROUTE_NAMES.includes(to.name as string)) {
      this.$store.commit("delKeepAlive", "NewJielong");
    }
    next();
  }

  beforeDestroy (): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
    this.teardownAndroidKeyboardGuard();
  }

  /**
   * 安卓键盘弹起时：锁住 window 滚动，仅在 .new-jielong-content 内把焦点输入框滚入可视区，
   * 避免全局 listenKeyBord 的 scrollIntoView 把整页顶起。
   */
  private setupAndroidKeyboardGuard (): void {
    if (window.platform !== "1") return;

    const resetWindowScroll = () => {
      window.scrollTo(0, 0);
      if (document.body) document.body.scrollTop = 0;
      if (document.documentElement) document.documentElement.scrollTop = 0;
    };

    this.androidKbHandler = () => {
      resetWindowScroll();
      window.setTimeout(() => {
        resetWindowScroll();
        this.scrollActiveInputIntoContent();
      }, 100);
      window.setTimeout(() => {
        resetWindowScroll();
        this.scrollActiveInputIntoContent();
      }, 350);
    };

    this.androidKbFocusHandler = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") return;
      const root = this.$el as HTMLElement;
      if (!root.contains(target)) return;
      window.setTimeout(() => {
        resetWindowScroll();
        this.scrollActiveInputIntoContent();
      }, 100);
    };

    window.addEventListener("resize", this.androidKbHandler);
    this.$el.addEventListener("focusin", this.androidKbFocusHandler as any);
  }

  private teardownAndroidKeyboardGuard (): void {
    if (this.androidKbHandler) {
      window.removeEventListener("resize", this.androidKbHandler);
      this.androidKbHandler = null;
    }
    if (this.androidKbFocusHandler && this.$el) {
      this.$el.removeEventListener("focusin", this.androidKbFocusHandler as any);
      this.androidKbFocusHandler = null;
    }
  }

  /** 仅滚动内部容器，不触发 window / 祖先 scrollIntoView */
  private scrollActiveInputIntoContent (): void {
    const el = document.activeElement as HTMLElement | null;
    const container = this.$refs.scrollEl as HTMLElement | undefined;
    if (!el || !container) return;
    if (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA") return;
    if (!container.contains(el)) return;

    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const padding = 16;
    if (eRect.bottom > cRect.bottom - padding) {
      container.scrollTop += eRect.bottom - cRect.bottom + padding;
    } else if (eRect.top < cRect.top + padding) {
      container.scrollTop -= cRect.top + padding - eRect.top;
    }
  }

  private setupObserver (): void {
    const root = this.$refs.scrollEl as HTMLElement | undefined;
    if (!root) return;

    if (this.observer) {
      this.observer.disconnect();
    }

    const visibleMap: Record<string, boolean> = {};

    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.scrollLock) return;
        entries.forEach((entry) => {
          visibleMap[entry.target.id] = entry.isIntersecting;
        });
        const active = TAB_ORDER.find(tab => visibleMap[SECTION_IDS[tab]]);
        if (active) {
          this.activeTab = active;
        }
      },
      {
        root,
        rootMargin: "-20% 0px -55% 0px",
        threshold: 0,
      }
    );

    TAB_ORDER.forEach((tab) => {
      const section = document.getElementById(SECTION_IDS[tab]);
      if (section) this.observer!.observe(section);
    });
  }

  /** 从"选择商品"页确认返回后，把缓存的已选商品合并进 goods（按 goodsId 去重，避免重复添加） */
  private applySelectedGoodsFromCache (): void {
    const raw = window.localStorage.getItem(SELECTED_GOODS_CACHE_KEY);
    if (!raw) return;
    window.localStorage.removeItem(SELECTED_GOODS_CACHE_KEY);

    let selected: any[] = [];
    try {
      selected = JSON.parse(raw) || [];
    } catch (e) {
      return;
    }
    if (!selected.length) return;

    let goods = this.goods.map(item => {
      const fresh = selected.find(s => s.goodsId === item.goodsId);
      if (!fresh) return item;
      const image = pickGoodsImage(fresh);
      const nextPrice = pickJoinPrice(fresh);
      let changed = false;
      const next = { ...item };
      if (image && !item.goodsImage) {
        next.goodsImage = image;
        changed = true;
      }
      // 已有商品若接龙价为空，用选品页最新价格补齐
      if (item.joinPrice == null && nextPrice != null) {
        next.joinPrice = nextPrice;
        changed = true;
      }
      if (!item.sellUnitWay && fresh.sellUnitWay) {
        next.sellUnitWay = fresh.sellUnitWay;
        next.joinSellUnit = Number(fresh.sellUnit) || item.joinSellUnit || 1;
        changed = true;
      }
      const freshImgList = fresh.imgList || fresh.storeImageList;
      if (freshImgList && (!item.imgList || !item.imgList.length)) {
        next.imgList = freshImgList;
        changed = true;
      }
      return changed ? next : item;
    });

    const existingIds = new Set(goods.map(item => item.goodsId).filter(Boolean));
    const appended = selected
      .filter(item => item.goodsId && !existingIds.has(item.goodsId))
      .map((item, idx) => createGoodsItemFromSelected(item, goods.length + idx));

    if (appended.length) {
      goods = [...goods, ...appended];
    }
    this.goods = goods;
  }
}
